/**
 * Reports – Bill Modify Report.
 * GET /histopathology/reports/bill-modify/?from_date&to_date[&patient_name&bill_number&modified_by]
 * One row per BillModificationLog. Totals: rows_count, total_extra_received, total_refunded, net_delta.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, Tooltip, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import { ImagePaths } from "../../Utility/ImagePaths";
import BillModifyDetailPopup from "./BillModifyDetailPopup";
import "../../Styles/Configuration.css";

const viewImg = ImagePaths?.LabViewButton?.default ?? ImagePaths?.LabViewButton;

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 1.2, py: 0.9, minWidth: "9vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.1vw", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.15 }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.85vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function BillModifyReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({ rows_count: 0, total_extra_received: 0, total_refunded: 0, net_delta: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });
  const [detailPopup, setDetailPopup] = useState({ open: false, logId: null });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_BILL_MODIFY}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const t = res.data.totals || {};
          setTotals({
            rows_count: +t.rows_count || 0,
            total_extra_received: +t.total_extra_received || 0,
            total_refunded: +t.total_refunded || 0,
            net_delta: +t.net_delta || 0,
          });
        } else {
          setRows([]);
          setTotals({ rows_count: 0, total_extra_received: 0, total_refunded: 0, net_delta: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    // Normalize the query so "1,000" and "1000" both match a stored "1000.00".
    const qNumeric = q.replace(/[,\s]/g, "");
    const numMatch = (v) => {
      if (v === null || v === undefined || v === "") return false;
      const s = String(v).toLowerCase();
      if (s.includes(q)) return true;
      // Compare pure-digit forms of both sides so 1,000 / 1000 / 1000.00 all match.
      const norm = s.replace(/[,\s]/g, "");
      if (qNumeric && norm.includes(qNumeric)) return true;
      const abs = norm.replace(/^-/, "");
      return !!qNumeric && abs.includes(qNumeric);
    };
    return rows.filter((r) => {
      const textFields = [r.patient_name, r.bill_number, r.nxt_id, r.modified_by, r.reason,
                          r.extra_receipt_number, r.refund_receipt_number];
      if (textFields.filter(Boolean).some((v) => String(v).toLowerCase().includes(q))) return true;
      const amountFields = [r.previous_bill_amount, r.new_bill_amount, r.delta_amount,
                            r.extra_received_amount, r.refund_amount,
                            r.previous_received_amount, r.new_received_amount];
      return amountFields.some(numMatch);
    });
  }, [rows, search]);

  const columns = [
    {
      field: "modified_at", headerName: "Modified At", flex: 1.2, minWidth: 160,
      renderCell: (p) => {
        const raw = p.row?.modified_at || "-";
        const [datePart, ...timeParts] = raw.split(" ");
        const timePart = timeParts.join(" ");
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            <Typography variant="body2">{datePart}</Typography>
            {timePart && <Typography variant="body2">{timePart}</Typography>}
          </Box>
        );
      },
    },
    {
      field: "patient_name", headerName: "Patient Name", flex: 1.2, minWidth: 150,
      renderCell: (p) => (
        <Tooltip title={(p.row?.patient_name || "-").toUpperCase()} placement="top" arrow>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              width: "100%", whiteSpace: "nowrap", overflow: "hidden",
              textOverflow: "ellipsis", textTransform: "uppercase",
            }}
          >
            {p.row?.patient_name || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    { field: "bill_number", headerName: "Bill Number", flex: 0.9, minWidth: 130 },
    {
      field: "new_bill_amount", headerName: `New Amount (${CurrencySymbol})`, flex: 0.9, minWidth: 120,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.new_bill_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.new_bill_amount ?? 0, false),
    },
    {
      field: "delta_amount", headerName: `Difference (${CurrencySymbol})`, flex: 0.8, minWidth: 100,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.delta_amount ?? 0),
      renderCell: (p) => {
        const v = +(p.row.delta_amount ?? 0);
        return (
          <Typography sx={{ fontSize: "0.85vw", color: v > 0 ? "#2e7d32" : (v < 0 ? "#d32f2f" : "text.primary"), fontWeight: 600 }}>
            {CommonValidation.AmountFormat(v, false)}
          </Typography>
        );
      },
    },
    {
      field: "extra_received_amount", headerName: `Extra Received (${CurrencySymbol})`, flex: 1, minWidth: 130,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.extra_received_amount ?? 0),
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{CommonValidation.AmountFormat(p.row.extra_received_amount ?? 0, false)}</Typography>
          {p.row.extra_receipt_number && (
            <Typography variant="caption" color="text.secondary">{p.row.extra_receipt_number}</Typography>
          )}
        </Box>
      ),
    },
    {
      field: "refund_amount", headerName: `Refunded (${CurrencySymbol})`, flex: 1, minWidth: 120,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.refund_amount ?? 0),
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{CommonValidation.AmountFormat(p.row.refund_amount ?? 0, false)}</Typography>
          {p.row.refund_receipt_number && (
            <Typography variant="caption" color="text.secondary">{p.row.refund_receipt_number}</Typography>
          )}
        </Box>
      ),
    },
    { field: "modified_by", headerName: "Modified By", flex: 0.9, minWidth: 120 },
    {
      field: "reason", headerName: "Reason", flex: 1.2, minWidth: 160, sortable: false,
      renderCell: (p) => (
        <Tooltip title={p.row?.reason || "-"} placement="top" arrow>
          <Box sx={{ width: "100%", pr: "18px", minWidth: 0 }}>
            <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {p.row?.reason || "-"}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "action", headerName: "Action", flex: 0.6, minWidth: 90, sortable: false, filterable: false,
      align: "center", headerAlign: "center",
      renderCell: (p) => (
        <Tooltip title="View Before/After Detail" placement="top" arrow>
          <IconButton size="small" sx={{ p: 0.3 }} onClick={() => setDetailPopup({ open: true, logId: p.row.id })}>
            <Box component="img" sx={{ width: 28, height: 28 }} src={viewImg} alt="view" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <SummaryCard label="Modifications" value={totals.rows_count} />
        <SummaryCard label="Extra Received" value={CommonValidation.AmountFormat(totals.total_extra_received)} />
        <SummaryCard label="Refunded" value={CommonValidation.AmountFormat(totals.total_refunded)} />
        <SummaryCard label="Net Difference" value={CommonValidation.AmountFormat(totals.net_delta)} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "10vw" }}>
          <CommonDatePicker
            FromDate={fromDate} ToDate={toDate} DateIndex={dateIndex}
            HideAllMenu removeMarginRight
            SelectedDates={(from, to, idx) => { setFromDate(from); setToDate(to); setDateIndex(idx); }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "11vw" }} />
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={54}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      <BillModifyDetailPopup
        open={detailPopup.open}
        logId={detailPopup.logId}
        onClose={() => setDetailPopup({ open: false, logId: null })}
      />
    </Box>
  );
}
